import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { useCurrentTemplateStructure } from "pages/HandBook/hooks/useCurrentTemplateStructure";
import { getHandbooksSegments } from "pages/HandBook/model/thunks";
import { IHandBookSegment, IHandBookTemplate } from "pages/HandBook/model/types";
import { ICommonColumn } from "pages/HandBook/tabs/HandBooksTemplates/levels/templates/HandBooksTemplates";
import { handbooksModifyWithEditings } from "pages/HandBook/utils/handbooksModifyWithEditings";

import { useTypedSelector } from "app/store/typedUseSelector";

import { IIdAndName } from "types/idAndName";

import { useUrlModuleWithObjectId } from "utils/hooks/useUrlModuleWithObjectId";

const filterSegments = (segments: IIdAndName[], filter: { name?: string }) => {
  const nameFilter = filter.name?.toLowerCase().trim();
  if (!nameFilter) return segments;
  return segments.filter((segment) => {
    return segment.name.toLowerCase().includes(nameFilter);
  });
};

export const useHandbookTemplateSegments = () => {
  const history = useHistory();
  const routePrefix = useUrlModuleWithObjectId();
  const dispatch = useDispatch();
  const { templateId, sectionId } = useParams<{ templateId: string; sectionId: string }>();
  const [filter, setFilter] = useState<any>({});
  const hasListFilters = Object.keys(filter).length > 0;
  const segments = useTypedSelector((state) => state.handBookReducer.segments);
  const isEditing = useTypedSelector((state) => state.handBookReducer.templateEditings.isEditing);
  const segmentEditings = useTypedSelector((state) => state.handBookReducer.templateEditings.segments);
  const invalidationKey = useTypedSelector((state) => state.handBookReducer.invalidationKey);
  const isLoading = useTypedSelector((state) => state.handBookReducer.loadings)["getHandbooksSegments"];
  const isLoadingMore = false;
  const isLoadingApplyEditings = useTypedSelector((state) => state.handBookReducer.loadings)[
    "applyEditingHandbookTemplates"
  ];

  const { currentTemplate, segment, parentSegment } = useCurrentTemplateStructure({
    templateId,
    segmentId: sectionId,
  });

  const isSubSegments = Boolean(sectionId);

  const currentSegment = (segment || parentSegment) as IHandBookSegment;

  const displaySegments = useMemo(() => {
    if (!segments) return [];

    const candidates = (() => {
      if (isSubSegments && currentSegment) {
        return currentSegment.children || [];
      }
      return segments.filter((s) => s.children?.length);
    })();

    return handbooksModifyWithEditings(filterSegments(candidates, filter), segmentEditings);
  }, [segments, isSubSegments, currentSegment, filter, segmentEditings]);

  const total = displaySegments.length;

  useEffect(() => {
    dispatch(getHandbooksSegments(templateId));
  }, [invalidationKey, templateId]);

  const onChangeFilter = useCallback(
    (name: string, value: string) => setFilter((prev: any) => ({ ...prev, [name]: value })),
    []
  );

  const onRowClick = (id: number) => {
    if (isSubSegments) {
      // При нажатии на подсегмент, переходим в представление расценок с фильтром по сегменту
      history.push(`/${routePrefix}/handbook/templates/${templateId}/expenditures?segment=${id}`);
    } else {
      // При нажатии на родительский сегмент, переходим к его подсегментам
      history.push(`./${templateId}/${id}`);
    }
  };

  const onBack = () => {
    if (isSubSegments) {
      // Возвращаемся к родительским сегментам
      history.push(`../${templateId}`);
    } else {
      // Возвращаемся к списку шаблонов
      history.push(`../templates`);
    }
  };

  const onPagination = () => {
    // Для сегментов не требуется пагинация тк все загружены сразу
  };

  const columns: ICommonColumn<IHandBookSegment | IHandBookTemplate>[] = useMemo(
    () => [
      {
        header: {
          title: "Атрибут",
          filterName: "name",
        },
        render: (item) => item.name,
      },
    ],
    []
  );

  return {
    hasListFilters,
    isSubSegments,
    displaySegments,
    isLoading,
    isLoadingMore,
    isLoadingApplyEditings,
    total,
    filter,
    onChangeFilter,
    onRowClick,
    onPagination,
    columns,
    onBack,
    currentTemplate,
    currentSegment,
    templateId,
    segmentId: sectionId,
    isEditing,
  };
};
